import { LoadStatus } from '../load-status';
import { NotificationIdState } from '../utilities/notification-id.type';

/**
 * Feature key under which the recipients slice is registered in the root store.
 */
export const RECIPIENTS_FEATURE_KEY = 'recipients';

/**
 * Flat, model-free shape of a single recipient entry — exactly the set of primitives
 * `RecipientModel`'s constructor consumes. Kept flat rather than mirroring the nested
 * `JsonRecipientModel` shape so the slice never needs to know about `TextModel` /
 * `BooleanModel` / `DisplayDecorator` wrappers; see `recipients.converters.ts` for the
 * model boundary.
 */
export type RecipientEntryState = Readonly<{
    faxEmail: string;
    commCategory: string;
    isSelected: boolean;
    isEditable: boolean;
    isDeletable: boolean;
    isHidden: boolean;
    isFromEmailSet: boolean;
    isSubmitted: boolean;
}>;

/**
 * The recipients saved for a single storage id (see `recipientStorageId` in
 * `recipients.converters.ts`), mirroring the legacy `SavedRecipientsInterface` shape
 * without the `RecipientModel[]` payload.
 */
export type SavedRecipientsState = Readonly<{
    notificationId: NotificationIdState;
    recipients: RecipientEntryState[];
}>;

/**
 * Serializable recipients slice.
 *
 * `entries` is persisted through the generic slice-persistence layer (see
 * `recipients-store.module.ts`); `loadStatus` and `error` are ephemeral store-only
 * bookkeeping, never written to storage. This domain has no expiry and no
 * `lastSavedTimestamp` — do not add one.
 */
// `error` uses `null` instead of the repo's usual `undefined`: the root store's strict
// serializability runtime checks reject explicitly undefined values (see address).
export type RecipientsCollectionState = Readonly<{
    entries: SavedRecipientsState[];
    loadStatus: LoadStatus;
    error: string | null;
}>;

export const initialRecipientsCollectionState: RecipientsCollectionState = {
    entries: [],
    loadStatus: LoadStatus.Idle,
    error: null,
};

/**
 * Default field values for a new recipient entry, mirroring `RecipientModel`'s
 * constructor defaults. Used by the reducer's create-if-absent branch and by the
 * converters — never re-implemented as an object literal elsewhere.
 *
 * @param faxEmail the recipient's fax/email address
 * @param commCategory the communication category
 * @returns a new recipient entry with the legacy model's default flags
 */
export function createDefaultRecipientEntryState(faxEmail: string, commCategory: string): RecipientEntryState {
    return {
        faxEmail,
        commCategory,
        isSelected: true,
        isEditable: false,
        isDeletable: false,
        isHidden: false,
        isFromEmailSet: true,
        isSubmitted: false,
    };
}

/**
 * An empty saved-recipients entry for the given storage id, used by the reducer's
 * create-if-absent branch when a save/remove targets an id with no existing entry.
 *
 * @param notificationId the notification id
 * @returns a new, empty saved-recipients entry
 */
export function createDefaultSavedRecipients(notificationId: NotificationIdState): SavedRecipientsState {
    return { notificationId, recipients: [] };
}
